import { createDocument, createTheme, p, HeadingLevel } from '@skills/docx-export.jsx';
import { logoBlock } from '@generated/components/logoDocx';

export interface GuideEvent { category?: string; title: string; when: string; startsAt: string; venue: string; where: string; cost: string; readerBlurb: string; link: string }

/** Strictly chronological: dated items by start time, undated last. */
export function sortGuide<T extends { startsAt: string; when: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.startsAt || '9999').localeCompare(b.startsAt || '9999') || a.when.localeCompare(b.when));
}

/** "Friday, November 6" heading from a YYYY-MM-DD… start (no time-zone shift: it's already NY time). */
export function dayHeading(startsAt: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(startsAt);
  if (!m) return 'Dates to be announced';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' });
}

export const venueLine = (e: GuideEvent) => `${e.venue}${e.where && e.where !== '—' ? ` · ${e.where}` : ''}`;
export const cleanLink = (u: string) => u.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

/** Reader-facing listings guide, grouped by day — ready to typeset in the paper. */
export function buildSceneGuideDocx(events: GuideEvent[], prepared: string) {
  const t = createTheme({ primary: '1F2937', secondary: 'B91C1C', accent: '1D4ED8', neutral: '222222', font: 'Georgia' });
  const c: unknown[] = [...logoBlock(), p('The Scene', { heading: HeadingLevel.TITLE }), p('Underground music, readings, theater & counterculture downtown', { italic: true }), p(prepared.replace(/^The Scene · /, ''), { bold: true })];
  let day = '';
  sortGuide(events).forEach((e) => {
    const h = dayHeading(e.startsAt);
    if (h !== day) { day = h; c.push(p(h, { heading: HeadingLevel.HEADING_1 })); }
    if (e.category) c.push(p(e.category.toUpperCase(), { size: 14, bold: true, color: "B91C1C" }));
    c.push(p(e.title, { heading: HeadingLevel.HEADING_2 }));
    c.push(p(`${e.when} · ${venueLine(e)}`, { bold: true }));
    if (e.cost) c.push(p(e.cost, { italic: true }));
    if (e.readerBlurb) c.push(p(e.readerBlurb));
    if (e.link?.startsWith('http')) c.push(p(cleanLink(e.link), { size: 16, color: '555555' }));
  });
  if (!events.length) c.push(p('No upcoming events were listed.', { italic: true }));
  return createDocument({ title: prepared, headerText: `The Village Star-Revue · ${prepared}`, styles: t.styles, numbering: t.numbering, children: c });
}
